import { ExpandedState, getCoreRowModel, getExpandedRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, Row, SortingState, useReactTable } from "@tanstack/react-table";
import { useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";

const useDataTableCollapsibleRow = ({
  data,
  columns,
  variant,
  onExpand,
  onExpandStateChange,
  globalFilter,
  setGlobalFilter,
  rowSelection,
  setRowSelection,
  setPageNumber,
  setPageSize,
  serverSidePagination = false,
  totalPages,
  pageNumber,
  pageSize,
  totalRecords,
  hideFooter = false,
  allowMultipleExpansion,
  resetPageIndex,
  sorting: sortingProp,
  onSortingChange,
  enableSorting = true,
  manualSorting,
  ref
}: any) => {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [nestedExpanded, setNestedExpanded] = useState<Record<string, boolean>>({});
  const [pagination, setPagination] = useState({
    pageIndex: pageNumber,
    pageSize: pageSize,
  });
  const [uncontrolledSorting, setUncontrolledSorting] = useState<SortingState>([]);
  const sorting = sortingProp ?? uncontrolledSorting;

  const isSecondary = useMemo(() => variant === "secondary", [variant]);
  const computedTotalRecords = useMemo(
    () => (totalRecords !== undefined ? totalRecords : data.length),
    [totalRecords, data.length]
  );

  // Synchronize parent state when local pagination updates
  useEffect(() => {
    setPageNumber?.(pagination.pageIndex);
    setPageSize?.(pagination.pageSize);
  }, [pagination, setPageNumber, setPageSize]);

  // Handle deep nested expands via TanStack
  const toggleChildExpanded = useCallback((id: string) => {
    setNestedExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const isChildExpanded = useCallback(
    (id: string) => !!nestedExpanded[id],
    [nestedExpanded]
  );

  // Initialize TanStack Table
  const table = useReactTable({
    data,
    columns,
    state: {
      expanded,
      globalFilter,
      ...(!hideFooter && { pagination }),
      rowSelection,
      sorting,
    },
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      if (sortingProp === undefined) {
        setUncontrolledSorting(next);
      }
      onSortingChange?.(next);
    },
    enableSorting,
    enableSortingRemoval: true,
    manualSorting: manualSorting ?? !!serverSidePagination,
    onExpandedChange: (updater) => {
      setExpanded((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;

        if (!allowMultipleExpansion && typeof next === "object") {
          const latestID = Object.keys(next).find(
            (id) => !(prev as Record<string, boolean>)[id]
          );
          return latestID ? { [latestID]: true } : {};
        }

        return next;
      });
    },
    autoResetPageIndex: resetPageIndex,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: hideFooter ? undefined : getPaginationRowModel(),
    onRowSelectionChange: setRowSelection,
    manualPagination: serverSidePagination,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    ...(serverSidePagination && !hideFooter && { pageCount: totalPages }),
  });

  // Handle Row Expansion Trigger
  const handleRowExpand = useCallback(
    (row: Row<any>) => {
      const toggleHandler = row.getToggleExpandedHandler();
      toggleHandler();
      const nextState = !row.getIsExpanded();
      onExpand?.(row);
      onExpandStateChange?.(row, nextState);
    },
    [onExpand, onExpandStateChange]
  );

  // Expose imperative ref methods
  useImperativeHandle(
    ref,
    () => ({
      hasActiveExpansions: () => {
        const hasRootExpanded = Object.keys(expanded).length > 0;
        const hasNestedExpanded = Object.keys(nestedExpanded).length > 0;
        return hasRootExpanded || hasNestedExpanded;
      },
      expandAll: () => {
        table.toggleAllRowsExpanded(true);
        const newNestedState: Record<string, boolean> = {};

        const buildNestedKeys = (items: any[], parentId: string) => {
          items?.forEach((item, idx) => {
            const childRowId = `${parentId}_child_${idx}`;
            if (item?.children?.length > 0) {
              newNestedState[childRowId] = true;
              buildNestedKeys(item.children, childRowId);
            }
          });
        };

        table.getRowModel().rows?.forEach((row) => {
          if ((row.original as any)?.children) {
            buildNestedKeys((row.original as any).children, row.id);
          }
        });

        setNestedExpanded(newNestedState);
      },
      collapseAll: () => {
        table.toggleAllRowsExpanded(false);
        setNestedExpanded({});
      },
    }),
    [expanded, nestedExpanded, table]
  );

  return {
    isSecondary,
    computedTotalRecords,
    toggleChildExpanded,
    isChildExpanded,
    handleRowExpand,
    table,
    pagination,
    setPagination
  }
}

export default useDataTableCollapsibleRow;